import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Eye, Search, RefreshCw, FileText, CreditCard, Upload } from 'lucide-react';

function fmtDateTime(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface ValidationTransaction {
  id: number;
  nui: string;
  alumnoId: number;
  alumnoNombre: string;
  concepto: string;
  fechaPago: string;
  fechaRegistro: string;
  paymentMethod: string | null;
  referencia: string | null;
  imageUrl: string | null;
  paymentId: number | null;
  sourceType: string | null;
}

const SOURCE_LABELS: Record<string, { label: string; color: 'default' | 'secondary' | 'outline' }> = {
  manual: { label: 'Manual', color: 'default' },
  bulk_csv: { label: 'CSV Masivo', color: 'secondary' },
  bulk_file: { label: 'Archivo', color: 'outline' },
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  TRANSFER: 'Transferencia',
  CHECK: 'Cheque',
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  OTHER: 'Otro',
};

async function fetchTransactions(params: {
  sourceType: string;
  dateFrom: string;
  dateTo: string;
  search: string;
  page: number;
  pageSize: number;
}) {
  const q = new URLSearchParams();
  if (params.sourceType !== 'all') q.set('sourceType', params.sourceType);
  if (params.dateFrom) q.set('dateFrom', params.dateFrom);
  if (params.dateTo) q.set('dateTo', params.dateTo);
  if (params.search) q.set('search', params.search);
  q.set('page', String(params.page));
  q.set('pageSize', String(params.pageSize));

  const res = await fetch(`/api/administration/validation-transactions?${q}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Error al cargar transacciones');
  return res.json() as Promise<{ data: ValidationTransaction[]; total: number; page: number; pageSize: number }>;
}

export default function ValidationTransactions() {
  const [sourceType, setSourceType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [proofModal, setProofModal] = useState<{ id: number; isImage: boolean } | null>(null);
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [proofLoading, setProofLoading] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['validation-transactions', sourceType, dateFrom, dateTo, search, page],
    queryFn: () => fetchTransactions({ sourceType, dateFrom, dateTo, search, page, pageSize }),
  });

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') applySearch();
  };

  const openProof = useCallback(async (id: number, imageUrl: string) => {
    const ext = imageUrl.split('.').pop()?.toLowerCase() || '';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    setProofModal({ id, isImage });
    setProofLoading(true);
    setProofUrl(null);

    try {
      const res = await fetch(`/api/administration/validation-transactions/${id}/proof`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar evidencia');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setProofUrl(url);
    } catch {
      setProofUrl(null);
    } finally {
      setProofLoading(false);
    }
  }, []);

  const downloadProof = useCallback(async (id: number, imageUrl: string) => {
    try {
      const res = await fetch(`/api/administration/validation-transactions/${id}/proof`, { credentials: 'include' });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const ext = imageUrl.split('.').pop() || 'jpg';
      const a = document.createElement('a');
      a.href = url;
      a.download = `evidencia-pago-${id}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const downloadCsv = useCallback(() => {
    if (!data?.data?.length) return;

    const BOM = '\uFEFF';
    const headers = ['ID', 'Tipo', 'NUI', 'Alumno', 'Concepto', 'Fecha Pago', 'Fecha Registro', 'Método', 'Referencia'];
    const rows = data.data.map(t => [
      t.id,
      SOURCE_LABELS[t.sourceType ?? '']?.label ?? t.sourceType ?? '',
      t.nui,
      t.alumnoNombre,
      `"${(t.concepto ?? '').replace(/"/g, '""')}"`,
      t.fechaPago,
      t.fechaRegistro ? fmtDateTime(t.fechaRegistro) : '',
      t.paymentMethod ? PAYMENT_METHOD_LABELS[t.paymentMethod] ?? t.paymentMethod : '',
      t.referencia ?? '',
    ]);

    const csv = BOM + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transacciones-validacion-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              Transacciones de Validación
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data?.data?.length}>
                <Download className="h-4 w-4 mr-1" />
                Exportar CSV
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground mb-1 block">Tipo de validación</label>
              <Select value={sourceType} onValueChange={v => { setSourceType(v); setPage(1); }}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="bulk_csv">CSV Masivo (Tesorería)</SelectItem>
                  <SelectItem value="bulk_file">Archivo (Aspirantes)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Fecha desde</label>
              <Input
                type="date"
                className="h-9 w-36"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Fecha hasta</label>
              <Input
                type="date"
                className="h-9 w-36"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }}
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1 block">Buscar</label>
              <div className="flex gap-1">
                <Input
                  placeholder="NUI, concepto, referencia..."
                  className="h-9"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button size="sm" className="h-9 px-3" onClick={applySearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-10 text-muted-foreground text-sm">Cargando...</div>
          ) : !data?.data?.length ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              No hay transacciones de validación registradas.
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">ID</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>NUI</TableHead>
                      <TableHead>Alumno</TableHead>
                      <TableHead>Concepto</TableHead>
                      <TableHead>Fecha Pago</TableHead>
                      <TableHead>Fecha Registro</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead className="text-center">Evidencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map(t => {
                      const src = SOURCE_LABELS[t.sourceType ?? ''] ?? { label: t.sourceType ?? 'N/D', color: 'outline' as const };
                      const hasProof = !!t.imageUrl;
                      const isCsvFile = t.imageUrl?.toLowerCase().endsWith('.csv') ?? false;
                      return (
                        <TableRow key={t.id}>
                          <TableCell className="text-muted-foreground text-xs">{t.id}</TableCell>
                          <TableCell>
                            <Badge variant={src.color} className="text-xs whitespace-nowrap">
                              {src.label === 'Manual' && <CreditCard className="h-3 w-3 mr-1" />}
                              {src.label === 'CSV Masivo' && <Upload className="h-3 w-3 mr-1" />}
                              {src.label === 'Archivo' && <FileText className="h-3 w-3 mr-1" />}
                              {src.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{t.nui}</TableCell>
                          <TableCell className="text-sm">{t.alumnoNombre}</TableCell>
                          <TableCell className="text-xs max-w-[200px] truncate" title={t.concepto}>{t.concepto}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{t.fechaPago}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {t.fechaRegistro ? fmtDateTime(t.fechaRegistro) : '—'}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {t.paymentMethod
                              ? PAYMENT_METHOD_LABELS[t.paymentMethod] ?? t.paymentMethod
                              : t.sourceType === 'bulk_file'
                                ? <span className="text-muted-foreground italic">No aplica</span>
                                : '—'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {t.referencia
                              ? t.referencia
                              : t.sourceType === 'bulk_file'
                                ? <span className="text-muted-foreground italic">No registrada</span>
                                : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              {hasProof ? (
                                isCsvFile ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                    title="Descargar archivo CSV de validación"
                                    onClick={() => downloadProof(t.id, t.imageUrl!)}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    CSV
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      title="Ver evidencia"
                                      onClick={() => openProof(t.id, t.imageUrl!)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      title="Descargar evidencia"
                                      onClick={() => downloadProof(t.id, t.imageUrl!)}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </>
                                )
                              ) : t.sourceType === 'bulk_file' ? (
                                <span
                                  className="text-xs text-muted-foreground italic cursor-help"
                                  title="El archivo CSV de este registro fue procesado antes de que el sistema implementara el almacenamiento de archivos. Los nuevos archivos subidos sí quedarán disponibles para descarga."
                                >
                                  Sin archivo
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Mostrando {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} de {data.total}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(1)}>«</Button>
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</Button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pg = totalPages <= 5 ? i + 1 : page <= 3 ? i + 1 : page >= totalPages - 2 ? totalPages - 4 + i : page - 2 + i;
                    return (
                      <Button
                        key={pg}
                        variant={pg === page ? 'default' : 'outline'}
                        size="sm"
                        className="w-8"
                        onClick={() => setPage(pg)}
                      >
                        {pg}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</Button>
                  <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!proofModal} onOpenChange={open => { if (!open) { setProofModal(null); if (proofUrl) URL.revokeObjectURL(proofUrl); setProofUrl(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Evidencia de Pago</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {proofLoading && (
              <div className="flex justify-center py-12 text-muted-foreground text-sm">Cargando evidencia...</div>
            )}
            {!proofLoading && proofUrl && proofModal?.isImage && (
              <img
                src={proofUrl}
                alt="Evidencia de pago"
                className="max-w-full max-h-[60vh] mx-auto rounded-md border object-contain"
              />
            )}
            {!proofLoading && proofUrl && !proofModal?.isImage && (
              <iframe
                src={proofUrl}
                title="Evidencia PDF"
                className="w-full h-[60vh] rounded-md border"
              />
            )}
            {!proofLoading && !proofUrl && (
              <div className="text-center py-12 text-muted-foreground text-sm">No se pudo cargar la evidencia.</div>
            )}
            {!proofLoading && proofUrl && (
              <div className="mt-3 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => proofModal && downloadProof(proofModal.id, '')}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Descargar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
